import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = '@sidebarCollapsed';
// Abaixo deste breakpoint o conteúdo fica espremido com a sidebar expandida,
// por isso colapsamos automaticamente.
const WIDE_BREAKPOINT = '(min-width: 1280px)';

interface SidebarContextType {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  /** true quando o colapso foi forçado pela largura da viewport (não pelo usuário). */
  isAutoCollapsed: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const readStoredPreference = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY) === 'true';
};

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarCollapsed, setCollapsedState] = useState(false);
  const [isAutoCollapsed, setIsAutoCollapsed] = useState(false);
  // Preferência manual do usuário (em telas largas). Mantida em ref para que o
  // listener de matchMedia leia sempre o valor atual sem recriar o efeito.
  const preferenceRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    preferenceRef.current = readStoredPreference();

    const mql = window.matchMedia(WIDE_BREAKPOINT);

    const apply = (isWide: boolean) => {
      if (isWide) {
        // Tela larga: respeita a preferência salva do usuário.
        setIsAutoCollapsed(false);
        setCollapsedState(preferenceRef.current);
      } else {
        // Tela estreita: força colapso para não espremer o conteúdo.
        setIsAutoCollapsed(true);
        setCollapsedState(true);
      }
    };

    apply(mql.matches);

    const handler = (event: MediaQueryListEvent) => apply(event.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Toggle manual: persiste a preferência e aplica imediatamente.
  const setIsSidebarCollapsed = useCallback((collapsed: boolean) => {
    preferenceRef.current = collapsed;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, String(collapsed));
    }
    setCollapsedState(collapsed);
  }, []);

  return (
    <SidebarContext.Provider value={{ isSidebarCollapsed, setIsSidebarCollapsed, isAutoCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
